import { Component, Element, h, Host, Prop, State } from '@stencil/core';

import { PatternlibIconBoldNames } from '@/src/utils/generated/icons-link-bold';
import { ClassNames } from '@/src/utils/interfaces';
import { hasNamedSlot } from '@/src/utils/slots';

export type Target = 'blank' | 'parent' | 'self' | 'top' | undefined | null | '';
export type TargetPrefixed = '_blank' | '_parent' | '_self' | '_top' | null | undefined;

/**
 * @part patternlib-link-v2-label - Targets the link's label.
 */
@Component({
  tag: 'patternlib-link-v2',
  styleUrl: 'patternlib-link-v2.scss',
  shadow: true,
})
export class PatternlibLinkV2 {
  @Element() hostElement: HTMLPatternlibLinkV2Element;

  /**
   * Optional. Set the aria-label attribute.
   */
  @Prop({ attribute: 'aria-label' }) ariaLabel: string | null;

  /**
   * Set the Link label.
   */
  @Prop() label = '';

  /**
   * Set a hyperlink. (If not set, link will have an anchor but no focus)
   */
  @Prop() href = '';

  /**
   * For special cases where no anchor is needed
   */
  @Prop() noAnchorCase = false;

  /**
   * Define hyperlink's target. (If no underscore prefix is provided it will be added automatically)
   */
  @Prop() target: Target | TargetPrefixed = 'blank';

  /**
   * Set link's type. (Prominent: Bold appearance, Decent: Light appearance)
   */
  @Prop() type: 'prominent' | 'decent' = 'prominent';

  /**
   * Set link's variant. (Action: Icon left, Context: Arrow icon right, Cross: Underlined)
   */
  @Prop() variant: 'action' | 'context' | 'cross' = 'action';

  /**
   * Set link's active state.
   */
  @Prop() active = false;

  /**
   * Set link's hovered state.
   */
  @Prop() hovered = false;

  /**
   * Show loading spinner.
   */
  @Prop() loading = false;

  /**
   * Set link's disabled state.
   */
  @Prop() disabled = false;

  /**
   * Set an icon by icon name.
   */
  @Prop() icon = '';

  /**
   * Hide icon, if not needed.
   */
  @Prop() hideIcon = false;

  /**
   * Set the focus gap color.
   */
  @Prop() focusGapColor: 'transparent' | 'themeColor' = 'transparent';

  /**
   * Inverts colors for cases with dark background.
   */
  @Prop() colorInverted = false;

  /**
   * State to check if the icon slot is used.
   */
  @State() hasIconSlot = false;

  private getFormattedTarget(target: Target | TargetPrefixed): TargetPrefixed {
    target = !target ? '_blank' : target;
    return (target.startsWith('_') ? target : `_${target}`) as TargetPrefixed;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    const link = this.hostElement.shadowRoot?.querySelector('a');
    if (event.key === 'Enter' && this.href && link) {
      link.click();
    }
  };

  componentDidLoad(): void {
    this.updateSlot();
  }

  private updateSlot(): void {
    this.hasIconSlot = hasNamedSlot(this.hostElement, 'icon');
  }

  render() {
    const isTypeProminent = this.type === 'prominent';
    const isTypeDecent = this.type === 'decent';
    const isVariantContext = this.variant === 'context';
    const isVariantAction = this.variant === 'action';

    const iconSize = isTypeDecent ? '24px' : '12px';
    const iconName = isVariantContext ? 'arrow-right' : this.icon || 'plus';

    const _ariaLabel = this.ariaLabel && this.ariaLabel.trim() !== '' ? this.ariaLabel.trim() : undefined;

    const showLabel = this.label.length > 0;
    const showIcon = (position: string): boolean => {
      if (this.loading || this.hideIcon) {
        return false;
      }
      if ((position === 'left' && isVariantAction) || (position === 'right' && isVariantContext)) {
        return true;
      }
      return false;
    };

    const linkClass: ClassNames = {
      [this.type]: true,
      'link': true,
      'loading': this.loading,
      'disabled': this.disabled,
      'color-inverted': this.colorInverted,
      'hovered': this.hovered,
    };

    const contentClass: ClassNames = {
      'content-wrapper': true,
      'active': this.active,
      'underlined': this.variant === 'cross',
    };

    const renderIcon = () => {
      const renderRegularIcon = () => <patternlib-icon icon-name={iconName} size={iconSize}></patternlib-icon>;

      const renderBoldIcon = () => {
        if (typeof iconName === 'string' && PatternlibIconBoldNames.includes(iconName)) {
          return <patternlib-icon-bold icon-name={iconName} size={iconSize}></patternlib-icon-bold>;
        } else {
          return <patternlib-icon icon-name={iconName} size={iconSize} bold={true}></patternlib-icon>;
        }
      };

      if (isTypeProminent) {
        return renderBoldIcon();
      } else {
        return renderRegularIcon();
      }
    };

    const iconElement = (
      <div class="icon-wrapper">
        {this.hasIconSlot && (
          <slot
            name="icon"
            onSlotchange={() => {
              this.updateSlot();
            }}
          ></slot>
        )}
        {!this.hasIconSlot && renderIcon()}
      </div>
    );

    const loadingSpinnerElement = (
      <div class="icon-wrapper">
        <patternlib-spinner spin size={isTypeProminent ? 'xsm' : 'sm'} slot="icon" />
      </div>
    );

    const contentElement = (
      <div class={contentClass}>
        {this.loading && loadingSpinnerElement}
        {showIcon('left') && iconElement}
        {showLabel && <div part="patternlib-link-v2-label">{this.label}</div>}
        {showIcon('right') && iconElement}
      </div>
    );

    return (
      <Host>
        <div class={linkClass}>
          {!this.noAnchorCase && (
            <a
              class={{ 'focus-gap-theme-color': this.focusGapColor === 'themeColor' }}
              href={this.href || undefined}
              target={this.getFormattedTarget(this.target)}
              aria-disabled={this.disabled ? 'true' : undefined}
              aria-label={_ariaLabel}
              onKeyDown={this.handleKeyDown}
            >
              {contentElement}
            </a>
          )}

          {this.noAnchorCase && (
            <div class="no-anchor" tabindex={this.disabled ? '-1' : '0'}>
              {contentElement}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
